import { Alert, Button, Card, CardBody, Spinner } from '@foundation/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import styles from './widgets.index.module.css'
import { api, type Widget } from '@/lib/api'

export const Route = createFileRoute('/widgets/')({
  component: WidgetsIndex,
})

const LAYOUT_LABELS: Record<string, string> = {
  grid: 'Wall of Love',
  carousel: 'Carousel',
  list: 'List',
  masonry: 'Masonry',
  marquee: 'Marquee',
  badge: 'Badge',
}

function WidgetsIndex() {
  const [widgets, setWidgets] = useState<Widget[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchWidgets = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const response = await api.listWidgets()
        setWidgets(response.data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load widgets')
      } finally {
        setIsLoading(false)
      }
    }
    void fetchWidgets()
  }, [])

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="lg" />
        <p>Loading widgets...</p>
      </div>
    )
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>
  }

  if (widgets.length === 0) {
    return (
      <Card>
        <CardBody>
          <div className={styles.emptyState}>
            <div className={styles.emptyIconCircle}>
              <span className={styles.emptyIconEmoji}>&#129513;</span>
            </div>
            <h3 className={styles.emptyTitle}>No widgets yet</h3>
            <p className={styles.emptyDescription}>
              Widgets let you showcase testimonials on your website. Choose from layouts like Wall
              of Love, Carousel, Masonry, or Badge and embed them with a simple code snippet.
            </p>
            <div className={styles.emptyActions}>
              <Link to="/widgets/new">
                <Button variant="primary">Create Widget</Button>
              </Link>
            </div>
          </div>
        </CardBody>
      </Card>
    )
  }

  return (
    <>
      <div className={styles.header}>
        <p className={styles.subtitle}>
          {widgets.length} widget{widgets.length === 1 ? '' : 's'}
        </p>
        <Link to="/widgets/new">
          <Button variant="primary">Create Widget</Button>
        </Link>
      </div>

      <div className={styles.grid}>
        {widgets.map((widget) => (
          <Card key={widget.id} className={styles.widgetCard}>
            <CardBody>
              <div className={styles.cardHeader}>
                <h3 className={styles.widgetName}>{widget.name}</h3>
                <span className={styles.layoutBadge}>
                  {LAYOUT_LABELS[widget.layout] ?? widget.layout}
                </span>
              </div>
              <div className={styles.cardMeta}>
                <div className={styles.metaRow}>
                  <span className={styles.metaIcon}>&#128172;</span>
                  <span>Max {widget.maxTestimonials} testimonials</span>
                </div>
                <div className={styles.metaRow}>
                  <span className={styles.metaIcon}>&#128197;</span>
                  <span>Created {formatDate(widget.createdAt)}</span>
                </div>
                <div className={styles.metaRow}>
                  <span className={styles.metaIcon}>&#127912;</span>
                  <span>
                    {widget.cardStyle === 'shadow'
                      ? 'Shadow'
                      : widget.cardStyle === 'bordered'
                        ? 'Bordered'
                        : 'Minimal'}{' '}
                    style
                  </span>
                </div>
              </div>
              <div className={styles.cardActions}>
                <Link to="/widgets/$id/edit" params={{ id: widget.id }}>
                  <Button size="sm" variant="secondary">
                    Edit
                  </Button>
                </Link>
                <Link to="/widgets/$id/embed" params={{ id: widget.id }}>
                  <Button size="sm" variant="primary">
                    Get Embed Code
                  </Button>
                </Link>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>
    </>
  )
}
